<template>
  <div class="approve-invoice-page mt-5">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <router-link to="/dashboard">Dashboard</router-link>
        </li>
        <li class="is-active">
          <router-link to="/dashboard/invoices/approvals" aria-current="true">
            Approvals
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- Heading & Controls -->
    <div class="level">
      <div class="level-left">
        <h2 class="title is-4">Pending Invoices</h2>
      </div>
      <div class="level-right">
        <div class="field has-addons">
          <!-- Bulk Approval button appears only when more than one invoice is selected -->
          <div class="control" v-if="selectedInvoiceIds.length > 1">
            <button class="mr-2 button is-small is-primary" @click="bulkApprove">
              Bulk Approve
            </button>
          </div>
          <div class="control">
            <div class="select is-small">
              <select v-model.number="pageSize" @change="onPageSizeChange">
                <option v-for="size in pageSizes" :key="size" :value="size">
                  {{ size }} per page
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden Approve All Button: Shown only when key combination is pressed -->
    <div v-if="showHiddenApproveAll" class="mb-3">
      <button class="button is-danger is-small" @click="approveAllPendingInvoices">
        Approve All Pending Invoices
      </button>
    </div>

    <div class="column is-12">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <!-- Checkbox for select all -->
            <th style="width: 50px;">
              <input
                type="checkbox"
                @change="toggleSelectAll"
                :checked="allSelected"
              />
            </th>
            <th class="is-size-7">Sender Name</th>
            <th class="is-size-7">Doc Type</th>
            <th class="is-size-7">Doc Number</th>
            <th class="is-size-7">Issue Date</th>
            <th class="is-size-7">Receiver Name</th>
            <th class="is-size-7">Document Total</th>
            <th class="is-size-7">Source</th>
            <th class="is-size-7">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in pendingInvoices" :key="invoice.id">
            <!-- Checkbox for each invoice -->
            <td>
              <input
                type="checkbox"
                :value="invoice.uuid"
                v-model="selectedInvoiceIds"
              />
            </td>
            <td class="is-vcentered is-size-7">{{ invoice.supplier_name }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_type }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_number }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_date }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.customer.name }}</td>
            <td class="is-vcentered is-size-7">
              {{ formatCurrency(invoice.total_net_amount) }}
            </td>
            <td class="is-vcentered is-size-7">{{ invoice.source}}</td>
            <td>
              <div>
                <span
                  class="icon-container"
                  @click="previewInvoice(invoice)"
                  title="Preview Invoice"
                >
                  <font-awesome-icon icon="fa-eye" class="has-text-success"/>
                </span>
                <span

                  class="icon-container ml-4"
                  @click="confirmChangeStatus(invoice.uuid, 'Approved')"
                  title="Approve Invoice"
                >
                  <font-awesome-icon icon="check" class="has-text-warning"/>
                </span>
                <span

                  class="icon-container ml-4"
                  @click="openRejectionModal(invoice.uuid)"
                  title="Reject Invoice"
                >
                  <font-awesome-icon icon="times" class="has-text-danger"/>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination controls -->
    <div class="columns is-vcentered">
      <div class="column">
        <nav class="pagination is-centered is-small" role="navigation" aria-label="pagination">
          <a
            class="pagination-previous is-small"
            :class="{ 'is-disabled': currentPage === 1 }"
            @click="goToPage(currentPage - 1)"
          >
            Previous
          </a>
          <a
            class="pagination-next is-small"
            :class="{ 'is-disabled': currentPage === totalPages || totalPages === 0 }"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </a>
          <ul class="pagination-list">
            <li v-for="page in totalPages" :key="page">
              <a
                class="pagination-link is-small"
                :class="{ 'is-current': page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <InvoicePreview
      :showPreview="showPreview"
      :invoice="selectedInvoice"
      :sender="selectedSender"
      :customer="selectedCustomer"
      @close="showPreview = false"
      v-if="selectedInvoice && selectedCustomer"
    />

    <div class="modal" :class="{ 'is-active': showRejectionModal }">
      <div class="modal-background" @click="closeRejectionModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Rejection Reason</p>
          <button class="delete" aria-label="close" @click="closeRejectionModal"></button>
        </header>
        <section class="modal-card-body">
          <textarea
            class="textarea"
            v-model="rejectionReason"
            placeholder="Enter the reason for rejection"
          ></textarea>
        </section>
        <footer class="buttons modal-card-foot">
          <button class="button is-danger" @click="rejectInvoice">Reject</button>
          <button class="button" @click="closeRejectionModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';
import InvoicePreview from '@/components/CreateInvoice/InvoicePreview.vue';

export default {
  name: 'InvoiceApproval',
  components: {
    InvoicePreview
  },
  data() {
    return {
      pendingInvoices: [],
      showPreview: false,
      selectedInvoice: null,
      selectedCustomer: null,
      selectedSender: null,
      currentUserId: null,
      showRejectionModal: false,
      rejectionReason: '',
      invoiceToReject: null,
      currentPage: 1,
      pageSize: 10,
      pageSizes: [5, 10, 20, 50],
      totalItems: 0,
      // Holds the uuid of each selected invoice
      selectedInvoiceIds: [],
      // Controls display of the hidden "Approve All Pending Invoices" button
      showHiddenApproveAll: false
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
    allSelected() {
      return (
        this.pendingInvoices.length > 0 &&
        this.pendingInvoices.every(invoice =>
          this.selectedInvoiceIds.includes(invoice.uuid)
        )
      );
    }
  },
  created() {
    this.fetchCurrentUserId();
    this.fetchPendingInvoices();
    // Listen for the hidden key combination: Shift + Alt + A
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleKeydown(e) {
      // Check if Shift + Alt + A is pressed
      if (e.shiftKey && e.altKey && e.key.toUpperCase() === 'A') {
        this.showHiddenApproveAll = true;
      }
    },
    fetchCurrentUserId() {
      const userId = localStorage.getItem('userid');
      this.currentUserId = parseInt(userId, 10);
    },
    fetchPendingInvoices() {
      axios
        .get(`/api/v1/invoices/pending/?page=${this.currentPage}&page_size=${this.pageSize}`)
        .then(response => {
          this.pendingInvoices = response.data.results;
          this.totalItems = response.data.count;
          // Remove any selected invoice that is no longer on the current page
          this.selectedInvoiceIds = this.selectedInvoiceIds.filter(uuid =>
            this.pendingInvoices.some(invoice => invoice.uuid === uuid)
          );
        })
        .catch(error => {
          console.error('There was an error fetching the pending invoices!', error);
          toast({
            message: 'Error fetching pending invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all invoices on the current page
        this.selectedInvoiceIds = this.selectedInvoiceIds.filter(uuid =>
          !this.pendingInvoices.some(invoice => invoice.uuid === uuid)
        );
      } else {
        // Select all invoice uuids on the current page
        const uuids = this.pendingInvoices.map(invoice => invoice.uuid);
        this.selectedInvoiceIds = Array.from(new Set([...this.selectedInvoiceIds, ...uuids]));
      }
    },
    bulkApprove() {
      if (this.selectedInvoiceIds.length < 2) {
        toast({
          message: 'Please select more than one invoice for bulk approval!',
          type: 'is-warning',
          dismissible: true,
          pauseOnHover: true,
          duration: 3000,
          position: 'top-right'
        });
        return;
      }
      const confirmationMessage = `Are you sure you want to approve ${this.selectedInvoiceIds.length} invoices? This action is not reversible.`;
      if (!window.confirm(confirmationMessage)) {
        return;
      }
      // Approve each selected invoice
      Promise.all(
        this.selectedInvoiceIds.map(uuid =>
          axios.post(`/api/v1/integration/approve-invoice/${uuid}/`, { status: 'Approved' }, { withCredentials: true })
        )
      )
        .then(() => {
          toast({
            message: 'Selected invoices approved successfully!',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
          // Clear selection and refresh data
          this.selectedInvoiceIds = [];
          this.fetchPendingInvoices();
        })
        .catch(error => {
          console.error('Error approving invoices!', error);
          toast({
            message: 'Error approving selected invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    approveAllPendingInvoices() {
      const confirmationMessage = "Are you sure you want to approve ALL pending invoices? This action is not reversible.";
      if (!window.confirm(confirmationMessage)) {
        return;
      }
      // Approve every pending invoice (from the current page)
      Promise.all(
        this.pendingInvoices.map(invoice =>
          axios.post(`/api/v1/integration/approve-invoice/${invoice.uuid}/`, { status: 'Approved' }, { withCredentials: true })
        )
      )
        .then(() => {
          toast({
            message: 'All pending invoices approved successfully!',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
          this.showHiddenApproveAll = false;
          this.fetchPendingInvoices();
        })
        .catch(error => {
          console.error('Error approving all invoices!', error);
          toast({
            message: 'Error approving all pending invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchPendingInvoices();
      }
    },
    onPageSizeChange() {
      // Reset to the first page if the page size changes
      this.currentPage = 1;
      this.fetchPendingInvoices();
    },
    openRejectionModal(uuid) {
      this.invoiceToReject = uuid;
      this.showRejectionModal = true;
    },
    closeRejectionModal() {
      this.showRejectionModal = false;
      this.rejectionReason = '';
      this.invoiceToReject = null;
    },
    rejectInvoice() {
      this.confirmChangeStatus(this.invoiceToReject, 'Rejected', this.rejectionReason);
      this.closeRejectionModal();
    },
    confirmChangeStatus(invoiceUuid, status, rejectionReason = null) {
      const confirmationMessage = `Are you sure you want to mark this invoice ${status.toLowerCase()}? This action is not reversible.`;
      if (window.confirm(confirmationMessage)) {
        this.changeInvoiceStatus(invoiceUuid, status, rejectionReason);
      }
    },
    changeInvoiceStatus(invoiceUuid, status, rejectionReason = null) {
      axios
        .post(`/api/v1/integration/approve-invoice/${invoiceUuid}/`, { status, rejection_reason: rejectionReason }, { withCredentials: true })
        .then(() => {
          this.fetchPendingInvoices();
          toast({
            message: `Invoice ${status.toLowerCase()} successfully!`,
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        })
        .catch(error => {
          console.error(`Error ${status.toLowerCase()}ing invoice!`, error);
          toast({
            message: `Error ${status.toLowerCase()}ing invoice!`,
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    previewInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.selectedCustomer = invoice.customer;
      this.selectedSender = invoice.sender;
      this.showPreview = true;
    },
    formatCurrency(amount) {
      if (!amount) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MYR' }).format(amount);
    }
  }
};
</script>

<style>
.table th,
.table td {
  text-align: center;
}
.icon-container {
  cursor: pointer;
}
.pagination-link.is-small,
.pagination-previous.is-small,
.pagination-next.is-small {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
.pagination-previous.is-disabled,
.pagination-next.is-disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
